import type { Action } from "@core/features/actions";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { ActionButtons } from "./ActionButtons";

export type Props = {
  /** The action for this step, used to get title/description */
  action: Action;
  /** Default title if action.name is not set */
  defaultTitle?: string;
  /** Default description if action.description is not set */
  defaultDescription?: string;
  /** Called when user completes the step */
  onComplete: () => void;
  /** Called when user skips the step */
  onSkip?: () => void;
  /** Whether to show the heading section (default: true) */
  showHeading?: boolean;
  /** Whether to show action buttons at bottom (default: true) */
  showActionButtons?: boolean;
  /** Content to render in the step */
  children: React.ReactNode;
};

/**
 * Standardized layout wrapper for onboarding steps.
 * Provides consistent container styling, optional heading from action data,
 * and optional action buttons (Continue/Skip) at the bottom.
 */
export function OnboardingStepLayout({
  action,
  defaultTitle,
  defaultDescription,
  onComplete,
  onSkip,
  showHeading = true,
  showActionButtons = true,
  children,
}: Props) {
  const title = action.name || defaultTitle;
  const description = action.description || defaultDescription;
  const hasHeading = showHeading && (title || description);

  return (
    <div className="w-full flex flex-col gap-6 max-w-3xl mx-auto">
      {hasHeading ? (
        <SectionHeading
          title={title}
          description={description}
          titleVariant="h2"
          className="gap-12"
          headingClassName="gap-4"
        >
          {children}
        </SectionHeading>
      ) : (
        children
      )}
      {showActionButtons && (
        <ActionButtons onContinue={onComplete} onSkip={onSkip} />
      )}
    </div>
  );
}

export default OnboardingStepLayout;
